import React from 'react'
import { SESSION_INTERFACE_IDS } from '../../domain'
import { RTC_PROVIDERS } from '../../providerIds'
import { createRtcProviderRegistry } from '../../providers/rtc'
import { createLiveSessionController } from '../../session'
import { getParticipantTransportKey } from '../rtcSessionShellSupport'

const EMPTY_CONTROLLER_STATE = Object.freeze({
  participants: [],
  visibleParticipants: [],
  dominantParticipantId: null,
})
const rtcProviderRegistry = createRtcProviderRegistry()

function getLocalParticipantIdentitySignature(identity) {
  if (!identity) {
    return ''
  }

  return [
    identity.participantId || '',
    identity.userId || '',
    identity.actorType || '',
    identity.actorId || '',
    identity.displayName || '',
    identity.profilePicId || '',
    identity.role || '',
  ].join('|')
}

export function useRtcSessionShell({
  providerId = RTC_PROVIDERS.agora,
  sessionKey,
  surfacePolicyId = SESSION_INTERFACE_IDS.standardLive,
  localParticipantIdentity = null,
  providerInitialConfig = {},
} = {}) {
  const runtimeRef = React.useRef(null)
  const pendingRuntimeCommandsRef = React.useRef(null)
  const localParticipantIdentityRef = React.useRef(localParticipantIdentity)
  const providerInitialConfigRef = React.useRef(providerInitialConfig)
  const localParticipantIdentitySignature = getLocalParticipantIdentitySignature(
    localParticipantIdentity,
  )
  const [controllerState, setControllerState] = React.useState(EMPTY_CONTROLLER_STATE)
  const [manualDominantParticipantId, setManualDominantParticipantId] = React.useState(null)
  const [runtimeCapabilities, setRuntimeCapabilities] = React.useState({
    screenShare: false,
  })

  localParticipantIdentityRef.current = localParticipantIdentity
  providerInitialConfigRef.current = providerInitialConfig

  React.useEffect(() => {
    const fallbackProvider = rtcProviderRegistry.get(RTC_PROVIDERS.agora)
    const resolvedProvider =
      rtcProviderRegistry.get(providerId) || fallbackProvider
    const portBundle = resolvedProvider.createPortBundle({
      initialConfig: providerInitialConfigRef.current,
      localParticipantIdentity: localParticipantIdentityRef.current,
    })
    const controller = createLiveSessionController({
      rtcSession: portBundle.rtcSession,
      presence: portBundle.presence,
      videoBinder: portBundle.videoBinder,
      surfacePolicyId,
      sessionInterfaceId: surfacePolicyId,
      localParticipantIdentity: localParticipantIdentityRef.current,
    })
    const unsubscribe = controller.subscribe((snapshot) => {
      setControllerState(snapshot)
    })

    runtimeRef.current = {
      portBundle,
      controller,
    }
    if (pendingRuntimeCommandsRef.current !== null) {
      portBundle.compatibilityCallbacks?.['register-runtime-commands']?.(
        pendingRuntimeCommandsRef.current,
      )
      pendingRuntimeCommandsRef.current = null
    }
    setControllerState(controller.getState())

    return () => {
      unsubscribe()
      controller.destroy()
      portBundle.destroy().catch(() => {})

      if (runtimeRef.current?.controller === controller) {
        runtimeRef.current = null
      }
    }
  }, [
    providerId,
    sessionKey,
    surfacePolicyId,
  ])

  React.useEffect(() => {
    setManualDominantParticipantId(null)
    setRuntimeCapabilities({
      screenShare: false,
    })
    pendingRuntimeCommandsRef.current = null
  }, [providerId, sessionKey])

  React.useEffect(() => {
    const runtime = runtimeRef.current
    const nextLocalParticipantIdentity = localParticipantIdentityRef.current

    if (!runtime?.controller || !runtime?.portBundle || !nextLocalParticipantIdentity) {
      return
    }

    runtime.controller.setLocalParticipantIdentity(nextLocalParticipantIdentity)
    runtime.portBundle.presence
      .publishLocalParticipantMetadata(nextLocalParticipantIdentity)
      .catch(() => {})
  }, [localParticipantIdentitySignature])

  React.useEffect(() => {
    runtimeRef.current?.controller?.setSurfacePolicyId(surfacePolicyId)
  }, [surfacePolicyId])

  React.useEffect(() => {
    if (!manualDominantParticipantId) {
      return
    }

    const manualParticipantStillVisible = controllerState?.visibleParticipants?.some(
      (participant) => participant?.id === manualDominantParticipantId,
    )

    if (manualParticipantStillVisible) {
      return
    }

    setManualDominantParticipantId(null)
    runtimeRef.current?.controller?.setPreferredDominantParticipantId?.(null)
  }, [controllerState?.visibleParticipants, manualDominantParticipantId])

  const dominantParticipant = React.useMemo(() => {
    if (!controllerState?.dominantParticipantId) {
      return null
    }

    return (
      controllerState?.participants?.find(
        (participant) => participant?.id === controllerState.dominantParticipantId,
      ) || null
    )
  }, [controllerState?.dominantParticipantId, controllerState?.participants])

  const handleParticipantSnapshot = React.useCallback((snapshot) => {
    runtimeRef.current?.portBundle?.compatibilityCallbacks?.['participants-updated']?.(snapshot)
  }, [])

  const handleConnectionStateChange = React.useCallback(
    (sessionState, previousState, reason) => {
      runtimeRef.current?.portBundle?.compatibilityCallbacks?.['connection-state-change']?.(
        sessionState,
        previousState,
        reason,
      )
    },
    [],
  )
  const registerRuntimeCommands = React.useCallback((commands, sourceSessionHandle = null) => {
    const activePortBundle = runtimeRef.current?.portBundle || null
    const activeSessionHandle = activePortBundle?.sessionHandle || null

    if (
      sourceSessionHandle &&
      activeSessionHandle &&
      sourceSessionHandle !== activeSessionHandle
    ) {
      return
    }

    const nextScreenShareCapability =
      typeof commands?.startScreenShare === 'function' &&
      typeof commands?.stopScreenShare === 'function'

    setRuntimeCapabilities((previousCapabilities) => {
      if (previousCapabilities?.screenShare === nextScreenShareCapability) {
        return previousCapabilities
      }

      return {
        ...previousCapabilities,
        screenShare: nextScreenShareCapability,
      }
    })

    if (!activePortBundle?.compatibilityCallbacks?.['register-runtime-commands']) {
      pendingRuntimeCommandsRef.current = commands
      return
    }

    pendingRuntimeCommandsRef.current = null
    activePortBundle.compatibilityCallbacks['register-runtime-commands'](commands)
  }, [])
  const handleScreenShareStateChange = React.useCallback((active) => {
    runtimeRef.current?.portBundle?.compatibilityCallbacks?.['screen-share-state-changed']?.(
      active,
    )
  }, [])
  const setPreferredDominantParticipantId = React.useCallback((participantId) => {
    const nextParticipantId =
      typeof participantId === 'string' && participantId.trim() ? participantId.trim() : null

    setManualDominantParticipantId(nextParticipantId)
    runtimeRef.current?.controller?.setPreferredDominantParticipantId?.(nextParticipantId)
  }, [])
  const clearManualFocus = React.useCallback(() => {
    setManualDominantParticipantId(null)
    runtimeRef.current?.controller?.setPreferredDominantParticipantId?.(null)
  }, [])

  return {
    ...controllerState,
    providerId,
    dominantParticipant,
    manualDominantParticipantId,
    isManualFocusActive: Boolean(manualDominantParticipantId),
    rtcSession: runtimeRef.current?.portBundle?.rtcSession || null,
    sessionHandle: runtimeRef.current?.portBundle?.sessionHandle || null,
    videoBinder: runtimeRef.current?.portBundle?.videoBinder || null,
    runtimeCapabilities,
    registerRuntimeCommands,
    handleScreenShareStateChange,
    setPreferredDominantParticipantId,
    clearManualFocus,
    preferredDominantTransportKey: getParticipantTransportKey(
      dominantParticipant,
      providerId,
    ),
    handleParticipantSnapshot,
    handleConnectionStateChange,
  }
}
